import React, { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "gv_pwa_dismissed";

function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const onInstalled = () => { setShow(false); setDeferred(null); };
    window.addEventListener("appinstalled", onInstalled);
    // iOS Safari has no beforeinstallprompt — show a manual hint
    const isIOS = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(window.navigator.userAgent);
    if (isIOS && isSafari) { setIosHint(true); setShow(true); }
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  const dismiss = () => { setShow(false); localStorage.setItem(DISMISS_KEY, "1"); };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setDeferred(null); setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-20 left-1/2 -translate-x-1/2 lg:bottom-4 z-[60] w-[calc(100%-1.5rem)] max-w-md" data-testid="pwa-install-banner">
      <div className="flex items-center gap-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/95 backdrop-blur-xl p-3 shadow-2xl">
        <img src="/gv-icon-192.png" alt="GameView" className="w-11 h-11 rounded-xl shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">Install GameView Studio</div>
          {iosHint ? (
            <div className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1">Tap <Share className="w-3.5 h-3.5 inline" /> then “Add to Home Screen”.</div>
          ) : (
            <div className="text-xs text-[hsl(var(--muted-foreground))]">Play & create full-screen, right from your home screen.</div>
          )}
        </div>
        {!iosHint && (
          <Button size="sm" onClick={install} className="rounded-full shrink-0 bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" data-testid="pwa-install-btn">
            <Download className="w-4 h-4" /> Install
          </Button>
        )}
        <button onClick={dismiss} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] p-1 shrink-0" data-testid="pwa-install-dismiss" aria-label="Dismiss"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
